import { create } from "zustand";

import { SlotDetails } from "../types/create-slot";

import { toast } from "@/utils/customToast";
import { Slot } from "@/features/calendar/types/calendar.ts";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import {
  getWorkspaceTutorSubjectsWithLevels,
  SubjectWithLevels,
} from "@/features/calendar/api/Slots.ts";

type ModalStore = {
  isOpen: boolean;
  action: string | undefined;
  setIsOpen: (isOpen: boolean) => void;
  defaultDate: string | undefined;
  defaultTime: string | undefined;
  setDefaultDate: (date: string) => void;
  setDefaultTime: (time: string) => void;
  setAction: (action: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  slot: SlotDetails | Slot | undefined;
  setSlot: (slot: Slot, role?: string) => Promise<void>;
  subjectsWithLevels: SubjectWithLevels[] | undefined;
  setSubjectsWithLevels: (workspaceId: string, t: any) => Promise<void>;
};

const getSlotDetails = async (
  slot: Slot,
  set: (partial: ModalStore | Partial<ModalStore>) => void,
  role: string = "tutor",
) => {
  if (role === "student") return slot;
  try {
    set({ isLoading: true });
    const { data } = await axiosApiInstance.get<{ data: SlotDetails }>(
      `${API_ENDPOINTS.TUTOR.CALENDAR.GET_DATES}/${slot.id}/show`,
    );

    return data.data;
  } catch (error) {
    return slot;
  } finally {
    set({ isLoading: false });
  }
};
const fetchSubjectsWithLevels = async (
  workspaceId: string,
  set: (partial: ModalStore | Partial<ModalStore>) => void,
  t: any,
) => {
  try {
    set({ isLoading: true });
    const { data } = await getWorkspaceTutorSubjectsWithLevels(workspaceId);

    return data.data;
  } catch (error) {
    console.error("Failed to fetch subjects with levels:", error);
    toast.error(t("common:errors.genericErrorTitle"), {
      description: t("forms:slotReservation.failedToLoadSubjects"),
    });
  } finally {
    set({ isLoading: false });
  }
};

export const useCalendarModalStore = create<ModalStore>((set) => ({
  isOpen: false,
  isLoading: false,
  setIsLoading: (isLoading: boolean) => set({ isLoading: isLoading }),
  action: undefined,
  setAction: (action: string) => set({ action: action }),
  setIsOpen: (isOpen: boolean) => set({ isOpen: isOpen }),
  defaultDate: undefined,
  defaultTime: undefined,
  setDefaultDate: (date: string) => set({ defaultDate: date }),
  setDefaultTime: (time: string) => set({ defaultTime: time }),
  slot: undefined,
  setSlot: async (slot: Slot, role?: string) =>
    set({ slot: await getSlotDetails(slot, set, role) }),
  subjectsWithLevels: undefined,
  setSubjectsWithLevels: async (workspaceId: string, t: any) =>
    set({ subjectsWithLevels: await fetchSubjectsWithLevels(workspaceId, set, t) }),
}));
